// client/src/world/flora/primitives.ts
import * as THREE from "three";
import type { Rng } from "@hyperlight/shared";
import type { C3 } from "./types";

/** Non-indexed copy (some primitives already are — avoid the warning). */
export function flat(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  return geo.index ? geo.toNonIndexed() : geo;
}

/**
 * Paint a constant color attribute over a geometry (pre-merge). `sway` marks
 * how much the wind shader may rustle these vertices: 1 = loose foliage,
 * 0 = rigid bark/rock (the trunk still leans via the height term).
 */
export function paint(geo: THREE.BufferGeometry, c: C3, sway = 0): THREE.BufferGeometry {
  const n = geo.getAttribute("position").count;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    col[i * 3] = c[0];
    col[i * 3 + 1] = c[1];
    col[i * 3 + 2] = c[2];
  }
  geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
  const sw = new Float32Array(n);
  sw.fill(sway);
  geo.setAttribute("sway", new THREE.BufferAttribute(sw, 1));
  geo.deleteAttribute("uv");
  return geo;
}

/** Low-poly tube from a to b (flat-shaded after the final merge). */
export function tube(a: THREE.Vector3, b: THREE.Vector3, r0: number, r1: number, c: C3, sides = 5, sway = 0): THREE.BufferGeometry {
  const dir = b.clone().sub(a);
  const len = dir.length();
  const geo = flat(new THREE.CylinderGeometry(r1, r0, len, sides, 1));
  geo.translate(0, len / 2, 0);
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
  geo.applyQuaternion(q);
  geo.translate(a.x, a.y, a.z);
  return paint(geo, c, sway);
}

/** Displaced icosphere blob — canopy clumps, boulders. */
export function blob(rng: Rng, center: THREE.Vector3, r: number, c: C3, rough = 0.22, detail = 0, sway = 0): THREE.BufferGeometry {
  const geo = flat(new THREE.IcosahedronGeometry(r, detail));
  const pos = geo.getAttribute("position");
  // Displace the UNIQUE vertices consistently: hash by quantized position so
  // shared corners of the non-indexed soup move together (no cracks).
  const seen = new Map<string, number>();
  for (let i = 0; i < pos.count; i++) {
    const key = `${pos.getX(i).toFixed(3)}|${pos.getY(i).toFixed(3)}|${pos.getZ(i).toFixed(3)}`;
    let k = seen.get(key);
    if (k === undefined) {
      k = 1 + (rng() * 2 - 1) * rough;
      seen.set(key, k);
    }
    pos.setXYZ(i, pos.getX(i) * k, pos.getY(i) * k, pos.getZ(i) * k);
  }
  geo.translate(center.x, center.y, center.z);
  return paint(geo, c, sway);
}

/** Cone shell (conifer tiers). */
export function cone(y: number, r: number, h: number, c: C3, sides = 6, sway = 0): THREE.BufferGeometry {
  const geo = flat(new THREE.ConeGeometry(r, h, sides));
  geo.translate(0, y + h / 2, 0);
  return paint(geo, c, sway);
}

export const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);

export function shade(c: C3, k: number): C3 {
  return [c[0] * k, c[1] * k, c[2] * k];
}
